import React, { useState, useEffect } from "react";
import { api } from "../../services/api";
import { toast } from "react-hot-toast";
import {
    FaSave, FaPlus, FaTrash, FaEdit, FaUpload, FaRedo,
    FaEye, FaEyeSlash, FaArrowUp, FaArrowDown,
    FaTimes, FaPlay, FaImage, FaFilm, FaLayerGroup, FaBuilding,
    FaSlidersH
} from "react-icons/fa";

const ContentManager = ({ activeSection = "branding", onContentUpdated }) => {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Modal states for slides, videos, gallery items
    const [slideModalOpen, setSlideModalOpen] = useState(false);
    const [editingSlide, setEditingSlide] = useState(null);
    const [slideForm, setSlideForm] = useState({
        title: "",
        subtitle: "",
        tag: "COMMERCIAL GOBO PROJECTORS",
        image: "/assets/lumigo/projector-1.jpg",
        ctaPrimaryLabel: "Explore Products",
        ctaPrimaryLink: "/products",
        ctaSecondaryLabel: "Request a Quote",
        ctaSecondaryLink: "/contact",
        order: 1,
        isActive: true,
    });

    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [videoForm, setVideoForm] = useState({
        title: "",
        description: "",
        thumbnail: "/assets/lumigo/projector-1.jpg",
        videoUrl: "/assets/lumigo/video-demo-1.mp4",
        duration: "0:30",
        category: "Industrial",
        order: 1,
        isPublished: true,
    });

    const [galleryModalOpen, setGalleryModalOpen] = useState(false);
    const [editingGallery, setEditingGallery] = useState(null);
    const [galleryForm, setGalleryForm] = useState({
        title: "",
        caption: "",
        image: "/assets/lumigo/projector-1.jpg",
        category: "Industrial",
        order: 1,
        isPublished: true,
    });

    // Fetch site content
    const loadContent = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/api/admin/content");
            setContent(data);
        } catch (err) {
            console.error("Failed to load content for admin:", err);
            toast.error("Error loading content data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadContent();
    }, []);

    // File upload helper
    const handleFileUpload = async (e, onUrlReceived) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Size check: 25MB for video, 8MB for image
        const isVideo = file.type.startsWith("video/");
        const maxSize = isVideo ? 25 * 1024 * 1024 : 8 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error(`File is too large. Max size: ${isVideo ? "25MB" : "8MB"}`);
            return;
        }

        setUploading(true);
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const { data } = await api.post("/api/admin/upload", {
                    base64Data: reader.result,
                    filename: file.name,
                    fileType: file.type,
                });
                onUrlReceived(data.url);
                toast.success("File uploaded successfully!");
            } catch (err) {
                console.error("Upload error:", err);
                toast.error(err.response?.data?.message || "File upload failed");
            } finally {
                setUploading(false);
            }
        };
        reader.readAsDataURL(file);
    };

    // Save partial content updates
    const saveContentSection = async (sectionKey, sectionData, successMsg = "Content updated successfully!") => {
        setSaving(true);
        try {
            const payload = { [sectionKey]: sectionData };
            const { data } = await api.put("/api/admin/content", payload);
            setContent(data.content);
            toast.success(successMsg);
            if (onContentUpdated) onContentUpdated(data.content);
        } catch (err) {
            console.error("Content save error:", err);
            toast.error(err.response?.data?.message || "Failed to save content");
        } finally {
            setSaving(false);
        }
    };

    // Reset video section to seeded demo videos
    const handleResetVideos = async () => {
        if (!window.confirm("Are you sure you want to restore the 4 default demonstration videos? Custom video links in this section will be replaced.")) return;
        setSaving(true);
        try {
            const { data } = await api.post("/api/admin/content/reset-videos");
            setContent(prev => ({
                ...prev,
                videoSection: {
                    ...prev.videoSection,
                    videos: data.videos,
                }
            }));
            toast.success("Demo videos restored successfully!");
            if (onContentUpdated) onContentUpdated();
        } catch (err) {
            toast.error("Failed to reset demo videos");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !content) {
        return (
            <div className="py-12 text-center text-xs text-[#687482]">
                <FaRedo className="animate-spin inline-block mr-2 text-[#E3B629]" />
                Loading website management settings...
            </div>
        );
    }

    // ==========================================
    // 1. BRANDING & APPEARANCE TAB
    // ==========================================
    if (activeSection === "branding") {
        const branding = content.branding || {};

        const handleSaveBranding = (e) => {
            e.preventDefault();
            saveContentSection("branding", branding, "Brand identity & company details saved!");
        };

        const updateBrandingField = (field, value) => {
            setContent(prev => ({
                ...prev,
                branding: { ...prev.branding, [field]: value }
            }));
        };

        return (
            <form onSubmit={handleSaveBranding} className="space-y-6 animate-fadeIn">
                <div className="flex items-center justify-between pb-4 border-b border-[#E5E7EB]">
                    <div>
                        <h3 className="text-base font-bold text-[#263746] flex items-center gap-2">
                            <FaBuilding className="text-[#E3B629]" /> Brand Identity & Appearance
                        </h3>
                        <p className="text-xs text-[#687482]">
                            Control logos, favicons, brand taglines, and public company details without modifying code.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {uploading && (
                            <span className="text-[11px] text-[#E3B629] font-bold animate-pulse">
                                Uploading Media File...
                            </span>
                        )}
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-5 py-2.5 rounded-lg text-xs uppercase tracking-wider flex items-center gap-2 transition shadow-sm"
                        >
                            <FaSave /> {saving ? "Saving..." : "Save Identity"}
                        </button>
                    </div>
                </div>

                {/* Logo & Favicon Assets */}
                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 shadow-sm space-y-6">
                    <h4 className="text-sm font-bold text-[#263746] border-b border-[#E5E7EB] pb-2">
                        Official Logo & Favicon Assets
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Primary Logo (Light Backgrounds) */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-[#263746] block">
                                Main Logo (for Light / White Backgrounds)
                            </label>
                            <div className="h-28 rounded-xl bg-white border border-[#E5E7EB] flex items-center justify-center p-3 relative group">
                                <img
                                    src={branding.logoUrl || "/logo.png"}
                                    alt="Main Logo Preview"
                                    className="max-h-full max-w-full object-contain"
                                    onError={(e) => { e.target.onerror = null; e.target.src = "/logo.png"; }}
                                />
                            </div>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={branding.logoUrl || ""}
                                    onChange={(e) => updateBrandingField("logoUrl", e.target.value)}
                                    placeholder="/logo.png"
                                    className="flex-1 bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2 text-xs text-[#263746]"
                                />
                                <label className="cursor-pointer bg-[#263746] hover:bg-[#1a2530] text-white p-2.5 rounded-lg text-xs flex items-center gap-1 transition">
                                    <FaUpload />
                                    <input
                                        type="file"
                                        accept="image/png,image/svg+xml,image/jpeg"
                                        onChange={(e) => handleFileUpload(e, (url) => updateBrandingField("logoUrl", url))}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                            <span className="text-[10px] text-[#687482] block">
                                Transparent PNG with dark LUMI and gold GO.
                            </span>
                        </div>

                        {/* Dark Background Logo Variant */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-[#263746] block">
                                Dark Background Logo Variant
                            </label>
                            <div className="h-28 rounded-xl bg-[#141B22] border border-[#263746] flex items-center justify-center p-3 relative group">
                                <img
                                    src={branding.darkLogoUrl || branding.logoUrl || "/logo.png"}
                                    alt="Dark Logo Preview"
                                    className="max-h-full max-w-full object-contain"
                                    onError={(e) => { e.target.onerror = null; e.target.src = "/logo.png"; }}
                                />
                            </div>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={branding.darkLogoUrl || ""}
                                    onChange={(e) => updateBrandingField("darkLogoUrl", e.target.value)}
                                    placeholder="/logo.png"
                                    className="flex-1 bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2 text-xs text-[#263746]"
                                />
                                <label className="cursor-pointer bg-[#263746] hover:bg-[#1a2530] text-white p-2.5 rounded-lg text-xs flex items-center gap-1 transition">
                                    <FaUpload />
                                    <input
                                        type="file"
                                        accept="image/png,image/svg+xml,image/jpeg"
                                        onChange={(e) => handleFileUpload(e, (url) => updateBrandingField("darkLogoUrl", url))}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                            <span className="text-[10px] text-[#687482] block">
                                Used when header or container has dark background.
                            </span>
                        </div>

                        {/* Aperture Favicon Symbol */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-[#263746] block">
                                Aperture Icon / Favicon Symbol
                            </label>
                            <div className="h-28 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB] flex items-center justify-center p-3 relative group">
                                <img
                                    src={branding.faviconUrl || "/favicon.png"}
                                    alt="Favicon Preview"
                                    className="w-14 h-14 object-contain"
                                    onError={(e) => { e.target.onerror = null; e.target.src = "/favicon.png"; }}
                                />
                            </div>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={branding.faviconUrl || ""}
                                    onChange={(e) => updateBrandingField("faviconUrl", e.target.value)}
                                    placeholder="/favicon.png"
                                    className="flex-1 bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2 text-xs text-[#263746]"
                                />
                                <label className="cursor-pointer bg-[#263746] hover:bg-[#1a2530] text-white p-2.5 rounded-lg text-xs flex items-center gap-1 transition">
                                    <FaUpload />
                                    <input
                                        type="file"
                                        accept="image/png,image/x-icon"
                                        onChange={(e) => handleFileUpload(e, (url) => updateBrandingField("faviconUrl", url))}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                            <span className="text-[10px] text-[#687482] block">
                                Gold camera aperture icon used for favicon & navbar badge.
                            </span>
                        </div>
                    </div>
                </div>

                {/* Brand Positioning & Copy */}
                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 shadow-sm space-y-4">
                    <h4 className="text-sm font-bold text-[#263746] border-b border-[#E5E7EB] pb-2">
                        Brand Voice & Tagline
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="text-xs font-bold text-[#263746] block mb-1">Brand Display Name</label>
                            <input
                                type="text"
                                value={branding.brandName || "LUMIGO"}
                                onChange={(e) => updateBrandingField("brandName", e.target.value)}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746] font-bold"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#263746] block mb-1">Business Attribution</label>
                            <input
                                type="text"
                                value={branding.attribution || "By Fourlifters"}
                                onChange={(e) => updateBrandingField("attribution", e.target.value)}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746]"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#263746] block mb-1">Official Tagline</label>
                            <input
                                type="text"
                                value={branding.tagline || "YOUR BRAND. IN LIGHT."}
                                onChange={(e) => updateBrandingField("tagline", e.target.value)}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746] font-bold text-[#E3B629]"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-[#263746] block mb-1">Brand Positioning Statement</label>
                        <textarea
                            rows={2}
                            value={branding.positioningStatement || ""}
                            onChange={(e) => updateBrandingField("positioningStatement", e.target.value)}
                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746]"
                        />
                    </div>
                </div>

                {/* Corporate Details (Fourlifters) */}
                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 shadow-sm space-y-4">
                    <h4 className="text-sm font-bold text-[#263746] border-b border-[#E5E7EB] pb-2">
                        Company Details & Contact Information
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-[#263746] block mb-1">Company Legal Name</label>
                            <input
                                type="text"
                                value={branding.companyName || "Fourlifters Private Limited"}
                                onChange={(e) => updateBrandingField("companyName", e.target.value)}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746]"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#263746] block mb-1">GST Number</label>
                            <input
                                type="text"
                                value={branding.gstNo || "27AAECF6202G1ZX"}
                                onChange={(e) => updateBrandingField("gstNo", e.target.value)}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs font-mono text-[#263746]"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-[#263746] block mb-1">Corporate Address</label>
                        <input
                            type="text"
                            value={branding.address || ""}
                            onChange={(e) => updateBrandingField("address", e.target.value)}
                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746]"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="text-xs font-bold text-[#263746] block mb-1">Mobile / WhatsApp</label>
                            <input
                                type="text"
                                value={branding.mobileNo || branding.phone || "+91 8087680893"}
                                onChange={(e) => {
                                    updateBrandingField("mobileNo", e.target.value);
                                    updateBrandingField("phone", e.target.value);
                                }}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs font-mono text-[#263746]"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#263746] block mb-1">Official Emails</label>
                            <input
                                type="text"
                                value={branding.email || "fourlifters@gmail.com, sales@fourlifters.in"}
                                onChange={(e) => updateBrandingField("email", e.target.value)}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746]"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#263746] block mb-1">Corporate Website</label>
                            <input
                                type="text"
                                value={branding.website || "www.fourlifters.in"}
                                onChange={(e) => updateBrandingField("website", e.target.value)}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746]"
                            />
                        </div>
                    </div>
                </div>
            </form>
        );
    }

    // ==========================================
    // 2. HERO SLIDES TAB
    // ==========================================
    if (activeSection === "slides") {
        const slides = content.heroSlides || [];
        const carouselConfig = content.carouselConfig || { enabled: true, interval: 2000 };

        const handleSaveCarouselConfig = (updates) => {
            const newConfig = { ...carouselConfig, ...updates };
            saveContentSection("carouselConfig", newConfig, "Carousel rotation settings updated!");
        };

        const handleSaveAllSlides = (updatedSlides) => {
            saveContentSection("heroSlides", updatedSlides, "Hero slides updated successfully!");
        };

        const handleOpenAddSlide = () => {
            setEditingSlide(null);
            setSlideForm({
                title: "",
                subtitle: "",
                tag: "COMMERCIAL GOBO PROJECTORS",
                image: "/assets/lumigo/projector-1.jpg",
                ctaPrimaryLabel: "Explore Products",
                ctaPrimaryLink: "/products",
                ctaSecondaryLabel: "Request a Quote",
                ctaSecondaryLink: "/contact",
                order: slides.length + 1,
                isActive: true,
            });
            setSlideModalOpen(true);
        };

        const handleOpenEditSlide = (slide) => {
            setEditingSlide(slide);
            setSlideForm({ ...slide });
            setSlideModalOpen(true);
        };

        const handleDeleteSlide = (id) => {
            if (!window.confirm("Are you sure you want to remove this hero slide?")) return;
            const updated = slides.filter(s => (s.id || s._id) !== id);
            handleSaveAllSlides(updated);
        };

        const handleToggleSlideActive = (id) => {
            const updated = slides.map(s => {
                if ((s.id || s._id) === id) return { ...s, isActive: !s.isActive };
                return s;
            });
            handleSaveAllSlides(updated);
        };

        const handleMoveSlide = (idx, direction) => {
            if ((direction === -1 && idx === 0) || (direction === 1 && idx === slides.length - 1)) return;
            const updated = [...slides];
            const temp = updated[idx];
            updated[idx] = updated[idx + direction];
            updated[idx + direction] = temp;
            // update orders
            updated.forEach((s, i) => { s.order = i + 1; });
            handleSaveAllSlides(updated);
        };

        const handleSubmitSlideForm = (e) => {
            e.preventDefault();
            let updated;
            if (editingSlide) {
                updated = slides.map(s => (s.id || s._id) === (editingSlide.id || editingSlide._id) ? { ...s, ...slideForm } : s);
            } else {
                const newSlide = {
                    ...slideForm,
                    id: `slide-${Date.now()}`,
                };
                updated = [...slides, newSlide];
            }
            handleSaveAllSlides(updated);
            setSlideModalOpen(false);
        };

        return (
            <div className="space-y-6 animate-fadeIn">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#E5E7EB]">
                    <div>
                        <h3 className="text-base font-bold text-[#263746] flex items-center gap-2">
                            <FaLayerGroup className="text-[#E3B629]" /> Hero Carousel & Slide Management
                        </h3>
                        <p className="text-xs text-[#687482]">
                            Control slide rotation timing, imagery, headings, and CTA button destinations.
                        </p>
                    </div>
                    <button
                        onClick={handleOpenAddSlide}
                        className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-4 py-2.5 rounded-lg text-xs uppercase tracking-wider flex items-center gap-2 transition shadow-sm"
                    >
                        <FaPlus /> Add Hero Slide
                    </button>
                </div>

                {/* Carousel Rotation Timing Controls */}
                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-[#263746]">
                            <input
                                type="checkbox"
                                checked={carouselConfig.enabled !== false}
                                onChange={(e) => handleSaveCarouselConfig({ enabled: e.target.checked })}
                                className="accent-[#E3B629] w-4 h-4"
                            />
                            <span>Automatic Carousel Rotation Enabled</span>
                        </label>

                        <div className="flex items-center gap-2 text-xs">
                            <span className="text-[#687482]">Slide Interval:</span>
                            <select
                                value={carouselConfig.interval || 2000}
                                onChange={(e) => handleSaveCarouselConfig({ interval: Number(e.target.value) })}
                                className="bg-[#F7F5EF] border border-[#E5E7EB] rounded p-1.5 font-bold text-[#263746]"
                            >
                                <option value={1500}>1.5 Seconds</option>
                                <option value={2000}>2.0 Seconds (Default Standard)</option>
                                <option value={3000}>3.0 Seconds</option>
                                <option value={4000}>4.0 Seconds</option>
                                <option value={5000}>5.0 Seconds</option>
                            </select>
                        </div>
                    </div>

                    <span className="text-[11px] text-[#687482]">
                        Rotation pauses when user hovers or focuses on the slide.
                    </span>
                </div>

                {/* Slides List */}
                <div className="space-y-4">
                    {slides.map((slide, idx) => (
                        <div
                            key={slide.id || slide._id || idx}
                            className={`p-5 rounded-2xl border transition flex flex-col md:flex-row gap-5 items-start md:items-center justify-between ${slide.isActive !== false ? "bg-[#FFFFFF] border-[#E5E7EB] shadow-sm" : "bg-[#F7F5EF]/60 border-dashed border-[#E5E7EB] opacity-60"}`}
                        >
                            <div className="flex items-center gap-4 flex-1">
                                {/* Slide Image Thumbnail */}
                                <div className="w-28 h-20 rounded-xl overflow-hidden bg-[#141B22] border border-[#E5E7EB] flex-shrink-0 relative">
                                    <img
                                        src={slide.image || "/assets/lumigo/projector-1.jpg"}
                                        alt={slide.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                    />
                                    <span className="absolute bottom-1 left-1 bg-black/70 text-[#E3B629] text-[9px] font-bold px-1.5 py-0.5 rounded font-mono">
                                        #{idx + 1}
                                    </span>
                                </div>

                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[#E3B629] text-[10px] font-black uppercase tracking-wider">
                                            {slide.tag || "SLIDE"}
                                        </span>
                                        <span className={`text-[9px] font-bold px-2 py-0.2 rounded ${slide.isActive !== false ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                                            {slide.isActive !== false ? "Active" : "Hidden"}
                                        </span>
                                    </div>
                                    <h4 className="text-sm font-bold text-[#263746]">
                                        {slide.title}
                                    </h4>
                                    <p className="text-xs text-[#687482] line-clamp-1 max-w-xl">
                                        {slide.subtitle}
                                    </p>
                                    <div className="text-[10px] text-[#687482] pt-0.5 flex gap-4">
                                        <span>Primary: <strong className="text-[#263746]">{slide.ctaPrimaryLabel || "Explore"} &rarr; {slide.ctaPrimaryLink || "/products"}</strong></span>
                                        <span>Secondary: <strong className="text-[#263746]">{slide.ctaSecondaryLabel || "Quote"} &rarr; {slide.ctaSecondaryLink || "/contact"}</strong></span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions & Reorder */}
                            <div className="flex items-center gap-2 flex-shrink-0 w-full md:w-auto justify-end">
                                <button
                                    onClick={() => handleMoveSlide(idx, -1)}
                                    disabled={idx === 0}
                                    className="p-2 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] disabled:opacity-30 text-xs"
                                    title="Move Up"
                                >
                                    <FaArrowUp />
                                </button>
                                <button
                                    onClick={() => handleMoveSlide(idx, 1)}
                                    disabled={idx === slides.length - 1}
                                    className="p-2 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] disabled:opacity-30 text-xs"
                                    title="Move Down"
                                >
                                    <FaArrowDown />
                                </button>
                                <button
                                    onClick={() => handleToggleSlideActive(slide.id || slide._id)}
                                    className="p-2 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] text-xs"
                                    title={slide.isActive !== false ? "Hide Slide" : "Show Slide"}
                                >
                                    {slide.isActive !== false ? <FaEye /> : <FaEyeSlash />}
                                </button>
                                <button
                                    onClick={() => handleOpenEditSlide(slide)}
                                    className="px-3 py-1.5 rounded bg-[#F7F5EF] hover:bg-[#E3B629] text-[#263746] font-bold text-xs flex items-center gap-1 transition"
                                >
                                    <FaEdit /> Edit
                                </button>
                                <button
                                    onClick={() => handleDeleteSlide(slide.id || slide._id)}
                                    className="p-2 rounded bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs transition"
                                    title="Delete Slide"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* MODAL: ADD / EDIT HERO SLIDE */}
                {slideModalOpen && (
                    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl max-w-xl w-full p-6 shadow-2xl">
                            <div className="flex justify-between items-center pb-3 border-b border-[#E5E7EB] mb-4">
                                <h3 className="text-base font-bold text-[#263746]">
                                    {editingSlide ? "Edit Hero Slide" : "Add New Hero Slide"}
                                </h3>
                                <button onClick={() => setSlideModalOpen(false)} className="text-[#687482] hover:text-[#263746]">
                                    <FaTimes />
                                </button>
                            </div>

                            <form onSubmit={handleSubmitSlideForm} className="space-y-4 text-xs">
                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Slide Tag / Category</label>
                                    <input
                                        type="text"
                                        value={slideForm.tag}
                                        onChange={(e) => setSlideForm({ ...slideForm, tag: e.target.value })}
                                        placeholder="INDUSTRIAL LOGO PROJECTION"
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Main Heading</label>
                                    <input
                                        type="text"
                                        value={slideForm.title}
                                        onChange={(e) => setSlideForm({ ...slideForm, title: e.target.value })}
                                        placeholder="Bring Your Brand to Life"
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Supporting Subtext</label>
                                    <textarea
                                        rows={2}
                                        value={slideForm.subtitle}
                                        onChange={(e) => setSlideForm({ ...slideForm, subtitle: e.target.value })}
                                        placeholder="High-intensity industrial logo projectors engineered for warehouses..."
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        required
                                    />
                                </div>

                                {/* Slide Image URL + Upload */}
                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Slide Background Image</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={slideForm.image}
                                            onChange={(e) => setSlideForm({ ...slideForm, image: e.target.value })}
                                            placeholder="/assets/lumigo/projector-1.jpg"
                                            className="flex-1 bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                            required
                                        />
                                        <label className="cursor-pointer bg-[#263746] hover:bg-[#1a2530] text-white px-3 py-2 rounded text-xs flex items-center gap-1 transition">
                                            <FaUpload /> Upload
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileUpload(e, (url) => setSlideForm({ ...slideForm, image: url }))}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="font-bold text-[#263746] block mb-1">Primary CTA Label</label>
                                        <input
                                            type="text"
                                            value={slideForm.ctaPrimaryLabel}
                                            onChange={(e) => setSlideForm({ ...slideForm, ctaPrimaryLabel: e.target.value })}
                                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        />
                                    </div>
                                    <div>
                                        <label className="font-bold text-[#263746] block mb-1">Primary CTA Link</label>
                                        <input
                                            type="text"
                                            value={slideForm.ctaPrimaryLink}
                                            onChange={(e) => setSlideForm({ ...slideForm, ctaPrimaryLink: e.target.value })}
                                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="font-bold text-[#263746] block mb-1">Secondary CTA Label</label>
                                        <input
                                            type="text"
                                            value={slideForm.ctaSecondaryLabel}
                                            onChange={(e) => setSlideForm({ ...slideForm, ctaSecondaryLabel: e.target.value })}
                                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        />
                                    </div>
                                    <div>
                                        <label className="font-bold text-[#263746] block mb-1">Secondary CTA Link</label>
                                        <input
                                            type="text"
                                            value={slideForm.ctaSecondaryLink}
                                            onChange={(e) => setSlideForm({ ...slideForm, ctaSecondaryLink: e.target.value })}
                                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        />
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <label className="flex items-center gap-2 cursor-pointer text-[#263746] font-bold">
                                        <input
                                            type="checkbox"
                                            checked={slideForm.isActive}
                                            onChange={(e) => setSlideForm({ ...slideForm, isActive: e.target.checked })}
                                            className="accent-[#E3B629]"
                                        />
                                        <span>Show this slide on the active homepage rotation</span>
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
                                    <button
                                        type="button"
                                        onClick={() => setSlideModalOpen(false)}
                                        className="px-4 py-2 rounded border border-[#E5E7EB] hover:bg-[#F7F5EF] text-[#263746]"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-5 py-2 rounded bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-bold"
                                    >
                                        {editingSlide ? "Update Slide" : "Add Slide"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ==========================================
    // 3. VIDEO DEMONSTRATIONS TAB
    // ==========================================
    if (activeSection === "videos") {
        const videoSection = content.videoSection || {};
        const videos = videoSection.videos || [];

        const handleSaveAllVideos = (updatedVideos) => {
            const updatedSection = {
                ...videoSection,
                videos: updatedVideos,
            };
            saveContentSection("videoSection", updatedSection, "Video demonstration section updated!");
        };

        const handleOpenAddVideo = () => {
            setEditingVideo(null);
            setVideoForm({
                title: "",
                description: "",
                thumbnail: "/assets/lumigo/projector-1.jpg",
                videoUrl: "/assets/lumigo/video-demo-1.mp4",
                duration: "0:30",
                category: "Industrial",
                order: videos.length + 1,
                isPublished: true,
            });
            setVideoModalOpen(true);
        };

        const handleOpenEditVideo = (v) => {
            setEditingVideo(v);
            setVideoForm({ ...v });
            setVideoModalOpen(true);
        };

        const handleDeleteVideo = (id) => {
            if (!window.confirm("Are you sure you want to delete this video entry?")) return;
            const updated = videos.filter(v => (v.id || v._id) !== id);
            handleSaveAllVideos(updated);
        };

        const handleTogglePublishVideo = (id) => {
            const updated = videos.map(v => {
                if ((v.id || v._id) === id) return { ...v, isPublished: !v.isPublished };
                return v;
            });
            handleSaveAllVideos(updated);
        };

        const handleSubmitVideoForm = (e) => {
            e.preventDefault();
            let updated;
            if (editingVideo) {
                updated = videos.map(v => (v.id || v._id) === (editingVideo.id || editingVideo._id) ? { ...v, ...videoForm } : v);
            } else {
                const newVid = {
                    ...videoForm,
                    id: `vid-${Date.now()}`,
                };
                updated = [...videos, newVid];
            }
            handleSaveAllVideos(updated);
            setVideoModalOpen(false);
        };

        return (
            <div className="space-y-6 animate-fadeIn">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#E5E7EB]">
                    <div>
                        <h3 className="text-base font-bold text-[#263746] flex items-center gap-2">
                            <FaFilm className="text-[#E3B629]" /> Dedicated Video Demonstrations
                        </h3>
                        <p className="text-xs text-[#687482]">
                            Manage the 4 video demonstration cards displayed on the homepage with custom thumbnails, descriptions, and playable MP4s.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleResetVideos}
                            className="bg-white hover:bg-[#F7F5EF] text-[#263746] border border-[#E5E7EB] font-bold px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 transition"
                            title="Restore the 4 verified default local demo videos"
                        >
                            <FaRedo /> Reset to Seed Demo Videos
                        </button>
                        <button
                            onClick={handleOpenAddVideo}
                            className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-4 py-2 rounded-lg text-xs uppercase tracking-wider flex items-center gap-1.5 transition shadow-sm"
                        >
                            <FaPlus /> Add Video
                        </button>
                    </div>
                </div>

                {/* Section Title Editor */}
                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-5 shadow-sm space-y-3">
                    <h4 className="text-xs font-bold text-[#263746] uppercase tracking-wider">
                        Homepage Video Section Headers
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                        <div>
                            <label className="text-[#687482] block mb-1 font-bold">Section Heading</label>
                            <input
                                type="text"
                                value={videoSection.heading || "Projection Videos — See LUMIGO in Action"}
                                onChange={(e) => setContent(prev => ({
                                    ...prev,
                                    videoSection: { ...prev.videoSection, heading: e.target.value }
                                }))}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                            />
                        </div>
                        <div>
                            <label className="text-[#687482] block mb-1 font-bold">Section Subheading</label>
                            <input
                                type="text"
                                value={videoSection.subheading || ""}
                                onChange={(e) => setContent(prev => ({
                                    ...prev,
                                    videoSection: { ...prev.videoSection, subheading: e.target.value }
                                }))}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button
                            onClick={() => saveContentSection("videoSection", content.videoSection, "Section header saved!")}
                            className="bg-[#263746] text-white hover:bg-[#1a2530] text-xs font-bold px-4 py-2 rounded transition"
                        >
                            Save Heading
                        </button>
                    </div>
                </div>

                {/* Video Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {videos.map((vid, idx) => (
                        <div
                            key={vid.id || vid._id || idx}
                            className={`p-4 rounded-2xl border transition flex flex-col justify-between ${vid.isPublished !== false ? "bg-[#FFFFFF] border-[#E5E7EB] shadow-sm" : "bg-[#F7F5EF]/60 border-dashed border-[#E5E7EB] opacity-60"}`}
                        >
                            <div>
                                {/* Video Thumbnail Preview with Play Icon */}
                                <div className="relative aspect-video rounded-xl overflow-hidden bg-[#141B22] border border-[#E5E7EB] mb-3 group">
                                    <img
                                        src={vid.thumbnail || "/assets/lumigo/projector-1.jpg"}
                                        alt={vid.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                    />
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-[#E3B629] text-[#263746] flex items-center justify-center text-xs">
                                            <FaPlay className="ml-0.5" />
                                        </div>
                                    </div>
                                    <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] font-mono px-1.5 py-0.5 rounded">
                                        {vid.duration || "0:30"}
                                    </span>
                                    <span className="absolute top-1 left-1 bg-[#E3B629] text-[#263746] text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">
                                        {vid.category || "Video"}
                                    </span>
                                </div>

                                <h4 className="text-xs font-bold text-[#263746] line-clamp-1 mb-1">
                                    {vid.title}
                                </h4>
                                <p className="text-[11px] text-[#687482] line-clamp-2 leading-relaxed mb-3">
                                    {vid.description}
                                </p>
                                <div className="text-[10px] text-[#687482] font-mono truncate mb-2">
                                    {vid.videoUrl}
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
                                <button
                                    onClick={() => handleTogglePublishVideo(vid.id || vid._id)}
                                    className="text-xs text-[#263746] hover:text-[#E3B629] flex items-center gap-1"
                                >
                                    {vid.isPublished !== false ? <FaEye /> : <FaEyeSlash />}
                                    <span className="text-[10px]">{vid.isPublished !== false ? "Published" : "Hidden"}</span>
                                </button>

                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleOpenEditVideo(vid)}
                                        className="p-1.5 rounded bg-[#F7F5EF] hover:bg-[#E3B629] text-[#263746] text-xs transition"
                                        title="Edit Video"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteVideo(vid.id || vid._id)}
                                        className="p-1.5 rounded bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs transition"
                                        title="Delete Video"
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* MODAL: ADD / EDIT VIDEO */}
                {videoModalOpen && (
                    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl max-w-lg w-full p-6 shadow-2xl">
                            <div className="flex justify-between items-center pb-3 border-b border-[#E5E7EB] mb-4">
                                <h3 className="text-base font-bold text-[#263746]">
                                    {editingVideo ? "Edit Video Demonstration" : "Add Demonstration Video"}
                                </h3>
                                <button onClick={() => setVideoModalOpen(false)} className="text-[#687482] hover:text-[#263746]">
                                    <FaTimes />
                                </button>
                            </div>

                            <form onSubmit={handleSubmitVideoForm} className="space-y-4 text-xs">
                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Video Title</label>
                                    <input
                                        type="text"
                                        value={videoForm.title}
                                        onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                                        placeholder="Industrial Facade & Warehouse Projection"
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Short Description</label>
                                    <textarea
                                        rows={2}
                                        value={videoForm.description}
                                        onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                                        placeholder="High-power 150W LED gobo projector displaying crisp corporate emblem..."
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="font-bold text-[#263746] block mb-1">Category</label>
                                        <select
                                            value={videoForm.category}
                                            onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })}
                                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        >
                                            <option value="Industrial">Industrial</option>
                                            <option value="Architectural">Architectural</option>
                                            <option value="Hardware">Hardware</option>
                                            <option value="Safety">Safety Signage</option>
                                            <option value="Decorative">Decorative</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="font-bold text-[#263746] block mb-1">Duration (MM:SS)</label>
                                        <input
                                            type="text"
                                            value={videoForm.duration}
                                            onChange={(e) => setVideoForm({ ...videoForm, duration: e.target.value })}
                                            placeholder="0:30"
                                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        />
                                    </div>
                                </div>

                                {/* Thumbnail URL + Upload */}
                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Poster / Thumbnail Image</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={videoForm.thumbnail}
                                            onChange={(e) => setVideoForm({ ...videoForm, thumbnail: e.target.value })}
                                            placeholder="/assets/lumigo/projector-1.jpg"
                                            className="flex-1 bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                            required
                                        />
                                        <label className="cursor-pointer bg-[#263746] hover:bg-[#1a2530] text-white px-3 py-2 rounded text-xs flex items-center gap-1 transition">
                                            <FaUpload />
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileUpload(e, (url) => setVideoForm({ ...videoForm, thumbnail: url }))}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>

                                {/* Video URL + Upload */}
                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Video File (MP4 URL or Upload)</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={videoForm.videoUrl}
                                            onChange={(e) => setVideoForm({ ...videoForm, videoUrl: e.target.value })}
                                            placeholder="/assets/lumigo/video-demo-1.mp4"
                                            className="flex-1 bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                            required
                                        />
                                        <label className="cursor-pointer bg-[#263746] hover:bg-[#1a2530] text-white px-3 py-2 rounded text-xs flex items-center gap-1 transition">
                                            <FaUpload /> Upload MP4
                                            <input
                                                type="file"
                                                accept="video/mp4,video/webm"
                                                onChange={(e) => handleFileUpload(e, (url) => setVideoForm({ ...videoForm, videoUrl: url }))}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <label className="flex items-center gap-2 cursor-pointer text-[#263746] font-bold">
                                        <input
                                            type="checkbox"
                                            checked={videoForm.isPublished}
                                            onChange={(e) => setVideoForm({ ...videoForm, isPublished: e.target.checked })}
                                            className="accent-[#E3B629]"
                                        />
                                        <span>Publish this video to the public homepage</span>
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
                                    <button
                                        type="button"
                                        onClick={() => setVideoModalOpen(false)}
                                        className="px-4 py-2 rounded border border-[#E5E7EB] hover:bg-[#F7F5EF] text-[#263746]"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-5 py-2 rounded bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-bold"
                                    >
                                        {editingVideo ? "Update Video" : "Save Video"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ==========================================
    // 4. PHOTO GALLERY TAB
    // ==========================================
    if (activeSection === "gallery") {
        const gallery = content.gallery || [];

        const handleSaveAllGallery = (updatedGallery) => {
            saveContentSection("gallery", updatedGallery, "Photo gallery updated successfully!");
        };

        const handleOpenAddPhoto = () => {
            setEditingGallery(null);
            setGalleryForm({
                title: "",
                caption: "",
                image: "/assets/lumigo/projector-1.jpg",
                category: "Industrial",
                order: gallery.length + 1,
                isPublished: true,
            });
            setGalleryModalOpen(true);
        };

        const handleOpenEditPhoto = (item) => {
            setEditingGallery(item);
            setGalleryForm({ ...item });
            setGalleryModalOpen(true);
        };

        const handleDeletePhoto = (id) => {
            if (!window.confirm("Are you sure you want to delete this photo from the gallery?")) return;
            const updated = gallery.filter(g => (g.id || g._id) !== id);
            handleSaveAllGallery(updated);
        };

        const handleSubmitPhotoForm = (e) => {
            e.preventDefault();
            let updated;
            if (editingGallery) {
                updated = gallery.map(g => (g.id || g._id) === (editingGallery.id || editingGallery._id) ? { ...g, ...galleryForm } : g);
            } else {
                const newPhoto = {
                    ...galleryForm,
                    id: `gal-${Date.now()}`,
                };
                updated = [...gallery, newPhoto];
            }
            handleSaveAllGallery(updated);
            setGalleryModalOpen(false);
        };

        return (
            <div className="space-y-6 animate-fadeIn">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#E5E7EB]">
                    <div>
                        <h3 className="text-base font-bold text-[#263746] flex items-center gap-2">
                            <FaImage className="text-[#E3B629]" /> Photo Gallery & Portfolio
                        </h3>
                        <p className="text-xs text-[#687482]">
                            Upload and organize installation photography, optical hardware shots, and custom gobo portfolio items.
                        </p>
                    </div>

                    <button
                        onClick={handleOpenAddPhoto}
                        className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-4 py-2 rounded-lg text-xs uppercase tracking-wider flex items-center gap-1.5 transition shadow-sm"
                    >
                        <FaPlus /> Add Photo
                    </button>
                </div>

                {/* Photo Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {gallery.map((item, idx) => (
                        <div
                            key={item.id || item._id || idx}
                            className={`p-3 rounded-2xl border transition flex flex-col justify-between ${item.isPublished !== false ? "bg-[#FFFFFF] border-[#E5E7EB] shadow-sm" : "bg-[#F7F5EF]/60 border-dashed border-[#E5E7EB] opacity-60"}`}
                        >
                            <div>
                                <div className="relative aspect-square rounded-xl overflow-hidden bg-[#141B22] border border-[#E5E7EB] mb-2">
                                    <img
                                        src={item.image || "/assets/lumigo/projector-1.jpg"}
                                        alt={item.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                    />
                                    <span className="absolute top-1 left-1 bg-[#E3B629] text-[#263746] text-[8px] font-bold px-1.5 py-0.5 rounded uppercase">
                                        {item.category || "Item"}
                                    </span>
                                </div>
                                <h4 className="text-[11px] font-bold text-[#263746] line-clamp-1">
                                    {item.title}
                                </h4>
                                <p className="text-[10px] text-[#687482] line-clamp-1">
                                    {item.caption}
                                </p>
                            </div>

                            <div className="flex justify-between items-center pt-2 mt-2 border-t border-[#E5E7EB]">
                                <span className="text-[9px] text-[#687482]">Order: {item.order || idx + 1}</span>
                                <div className="flex gap-1.5">
                                    <button
                                        onClick={() => handleOpenEditPhoto(item)}
                                        className="p-1 rounded bg-[#F7F5EF] hover:bg-[#E3B629] text-[#263746] text-[10px]"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        onClick={() => handleDeletePhoto(item.id || item._id)}
                                        className="p-1 rounded bg-rose-50 hover:bg-rose-100 text-rose-600 text-[10px]"
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* MODAL: ADD / EDIT PHOTO */}
                {galleryModalOpen && (
                    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl max-w-md w-full p-6 shadow-2xl">
                            <div className="flex justify-between items-center pb-3 border-b border-[#E5E7EB] mb-4">
                                <h3 className="text-base font-bold text-[#263746]">
                                    {editingGallery ? "Edit Gallery Item" : "Add Photo to Gallery"}
                                </h3>
                                <button onClick={() => setGalleryModalOpen(false)} className="text-[#687482] hover:text-[#263746]">
                                    <FaTimes />
                                </button>
                            </div>

                            <form onSubmit={handleSubmitPhotoForm} className="space-y-4 text-xs">
                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Image Title</label>
                                    <input
                                        type="text"
                                        value={galleryForm.title}
                                        onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                                        placeholder="Heavy Industry Crane Alert"
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Caption</label>
                                    <input
                                        type="text"
                                        value={galleryForm.caption}
                                        onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })}
                                        placeholder="High-contrast safety zone projection..."
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                    />
                                </div>

                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Category</label>
                                    <select
                                        value={galleryForm.category}
                                        onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                    >
                                        <option value="Industrial">Industrial</option>
                                        <option value="Architectural">Architectural</option>
                                        <option value="Decorative">Decorative</option>
                                        <option value="Hardware">Hardware</option>
                                        <option value="Safety">Safety</option>
                                        <option value="Hospitality">Hospitality</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="font-bold text-[#263746] block mb-1">Image URL or Upload</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={galleryForm.image}
                                            onChange={(e) => setGalleryForm({ ...galleryForm, image: e.target.value })}
                                            placeholder="/assets/lumigo/projector-1.jpg"
                                            className="flex-1 bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2 text-[#263746]"
                                            required
                                        />
                                        <label className="cursor-pointer bg-[#263746] hover:bg-[#1a2530] text-white px-3 py-2 rounded text-xs flex items-center gap-1 transition">
                                            <FaUpload />
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileUpload(e, (url) => setGalleryForm({ ...galleryForm, image: url }))}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
                                    <button
                                        type="button"
                                        onClick={() => setGalleryModalOpen(false)}
                                        className="px-4 py-2 rounded border border-[#E5E7EB] hover:bg-[#F7F5EF] text-[#263746]"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-5 py-2 rounded bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-bold"
                                    >
                                        Save Photo
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ==========================================
    // 5. CONTENT & SECTION VISIBILITY TAB
    // ==========================================
    if (activeSection === "content") {
        const vis = content.sectionVisibility || {};
        const aboutContent = content.aboutContent || {};

        const handleSaveVisibility = (e) => {
            e.preventDefault();
            saveContentSection("sectionVisibility", vis, "Homepage section visibility updated!");
        };

        const handleSaveAbout = (e) => {
            e.preventDefault();
            saveContentSection("aboutContent", aboutContent, "About LUMIGO copy saved!");
        };

        const toggleSection = (sectionKey) => {
            setContent(prev => ({
                ...prev,
                sectionVisibility: {
                    ...prev.sectionVisibility,
                    [sectionKey]: prev.sectionVisibility?.[sectionKey] === false ? true : false,
                }
            }));
        };

        return (
            <div className="space-y-6 animate-fadeIn">
                <div className="pb-4 border-b border-[#E5E7EB]">
                    <h3 className="text-base font-bold text-[#263746] flex items-center gap-2">
                        <FaSlidersH className="text-[#E3B629]" /> Homepage Sections & Copy Controls
                    </h3>
                    <p className="text-xs text-[#687482]">
                        Toggle visibility of individual homepage modules and edit marketing narrative.
                    </p>
                </div>

                {/* Section Visibility Switches */}
                <form onSubmit={handleSaveVisibility} className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
                        <h4 className="text-sm font-bold text-[#263746]">
                            Homepage Module Visibility
                        </h4>
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-bold px-4 py-2 rounded-lg text-xs transition"
                        >
                            Save Visibility
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
                        {[
                            { key: "heroCarousel", label: "1. Hero Image Carousel" },
                            { key: "benefitsStrip", label: "2. 5-Card Benefits Strip" },
                            { key: "featuredProduct", label: "3. Featured Hardware Highlight (DXAX-150T)" },
                            { key: "applications", label: "4. Projection Applications" },
                            { key: "productCatalogue", label: "5. Featured Product Grid" },
                            { key: "videoSection", label: "6. Dedicated Video Demonstrations" },
                            { key: "photoGallery", label: "7. Photo Gallery Grid" },
                            { key: "customGoboCta", label: "8. Custom Gobo / Artwork Banner" },
                            { key: "aboutSection", label: "9. About LUMIGO / What We Do" },
                        ].map(sec => (
                            <label
                                key={sec.key}
                                className="flex items-center justify-between p-3 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB] cursor-pointer hover:border-[#E3B629] transition"
                            >
                                <span className="font-bold text-[#263746]">{sec.label}</span>
                                <input
                                    type="checkbox"
                                    checked={vis[sec.key] !== false}
                                    onChange={() => toggleSection(sec.key)}
                                    className="accent-[#E3B629] w-4 h-4 cursor-pointer"
                                />
                            </label>
                        ))}
                    </div>
                </form>

                {/* About LUMIGO Narrative Editor */}
                <form onSubmit={handleSaveAbout} className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
                        <h4 className="text-sm font-bold text-[#263746]">
                            About LUMIGO Section Narrative
                        </h4>
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-[#263746] text-white hover:bg-[#1a2530] font-bold px-4 py-2 rounded-lg text-xs transition"
                        >
                            Save About Copy
                        </button>
                    </div>

                    <div className="space-y-4 text-xs">
                        <div>
                            <label className="font-bold text-[#263746] block mb-1">Headline</label>
                            <input
                                type="text"
                                value={aboutContent.title || ""}
                                onChange={(e) => setContent(prev => ({
                                    ...prev,
                                    aboutContent: { ...prev.aboutContent, title: e.target.value }
                                }))}
                                placeholder="Engineered for Industrial Reliability & Architectural Brilliance"
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2.5 text-[#263746]"
                            />
                        </div>

                        <div>
                            <label className="font-bold text-[#263746] block mb-1">Paragraph 1 (Core Focus)</label>
                            <textarea
                                rows={3}
                                value={aboutContent.paragraph1 || ""}
                                onChange={(e) => setContent(prev => ({
                                    ...prev,
                                    aboutContent: { ...prev.aboutContent, paragraph1: e.target.value }
                                }))}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2.5 text-[#263746]"
                            />
                        </div>

                        <div>
                            <label className="font-bold text-[#263746] block mb-1">Paragraph 2 (Engineering & Optical Standards)</label>
                            <textarea
                                rows={3}
                                value={aboutContent.paragraph2 || ""}
                                onChange={(e) => setContent(prev => ({
                                    ...prev,
                                    aboutContent: { ...prev.aboutContent, paragraph2: e.target.value }
                                }))}
                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded p-2.5 text-[#263746]"
                            />
                        </div>
                    </div>
                </form>
            </div>
        );
    }

    return null;
};

export default ContentManager;
